import { Pressable } from "@/components/ui/pressable";
import { cn } from "@/lib/utils";
import {
  ALL_WORK_CATEGORIES,
  areAllWorkCategoriesSelected,
} from "@/modules/work/useWorkPreferences";
import type { WorkCategory } from "@/modules/work/workTypes";
import {
  getFilterGroupCount,
  isFilterGroupFullySelected,
  isOnlyFilterGroupSelected,
  resolveFilterGroupSelection,
  WORK_CALENDAR_FILTER_GROUPS,
  type WorkCalendarFilterGroup,
} from "@/modules/work/workCalendarFilterGroups";

export const WorkCategoryChips = ({
  selected,
  onChange,
  counts,
}: {
  selected: WorkCategory[];
  onChange: (next: WorkCategory[]) => void;
  counts?: Partial<Record<WorkCategory, number>>;
}) => {
  const allSelected = areAllWorkCategoriesSelected(selected);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {!allSelected ? (
        <Pressable
          onClick={() => onChange([...ALL_WORK_CATEGORIES])}
          className="inline-flex h-7 items-center rounded-md border border-border bg-card px-2 text-xs text-foreground transition-colors hover:bg-muted"
        >
          All
        </Pressable>
      ) : null}
      {WORK_CALENDAR_FILTER_GROUPS.map((group) => {
        const active = isFilterGroupFullySelected(selected, group);
        const solo = isOnlyFilterGroupSelected(selected, group);
        const count = counts ? getFilterGroupCount(counts, group) : undefined;

        return (
          <Pressable
            key={group.id}
            title={group.title}
            aria-pressed={active}
            onClick={() =>
              onChange(
                resolveFilterGroupSelection(
                  selected,
                  group.id as WorkCalendarFilterGroup,
                ),
              )
            }
            // Neutral chips: the dot carries the color; off chips are dashed.
            className={cn(
              "inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-xs transition-colors",
              active
                ? "border-border bg-card text-foreground hover:bg-muted"
                : "border-dashed text-muted-foreground hover:text-foreground",
              solo && "border-primary",
            )}
          >
            <span
              className={cn(
                "size-2 shrink-0 rounded-full",
                active ? group.dotClass : "bg-muted-foreground/40",
              )}
              aria-hidden
            />
            {group.label}
            {typeof count === "number" ? (
              <span className="tabular-nums text-muted-foreground">
                {count}
              </span>
            ) : null}
          </Pressable>
        );
      })}
    </div>
  );
};
